import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import "../css/Users.css";

function normalizeUsername(value) {
  return String(value || "").trim().toLowerCase();
}

export default function Users({ currentUser }) {
  const isAdmin = currentUser?.role === "admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPins, setShowPins] = useState(false);

  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  const [newUser, setNewUser] = useState({
    username: "",
    pin: "",
    confirmPin: "",
    role: "user",
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const snap = await getDocs(collection(db, "appUsers"));
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsers(rows);
    } catch (e) {
      console.error("Error fetching users:", e);
      setError("Could not load users. See console for details.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchUsers();
  }, [isAdmin]);

  const sorted = useMemo(() => {
    return (users || [])
      .slice()
      .sort((a, b) => normalizeUsername(a.username).localeCompare(normalizeUsername(b.username)));
  }, [users]);

  const startEdit = (u) => {
    setEditingId(u.id);
    setDraft({
      username: u.username || "",
      pin: u.pin || "",
      confirmPin: u.pin || "",
      role: u.role || "user",
      active: u.active !== false,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const saveEdit = async (u) => {
    if (!u?.id) return;
    setBusyId(u.id);
    try {
      const payload = {
        username: normalizeUsername(draft.username),
        pin: String(draft.pin || "").trim(),
        role: draft.role === "admin" ? "admin" : "user",
        active: draft.active !== false,
        updatedAt: serverTimestamp(),
      };
      if (!payload.username) {
        alert("Username is required.");
        return;
      }
      if (!payload.pin) {
        alert("PIN is required.");
        return;
      }
      if (String(draft.confirmPin || "").trim() !== payload.pin) {
        alert("PIN fields do not match.");
        return;
      }
      await updateDoc(doc(db, "appUsers", u.id), payload);
      setUsers((prev) => (prev || []).map((row) => (row.id === u.id ? { ...row, ...payload } : row)));
      cancelEdit();
    } catch (e) {
      console.error("Error updating user:", e);
      alert("Failed to update user. See console for details.");
    } finally {
      setBusyId(null);
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    const username = normalizeUsername(newUser.username);
    const pin = String(newUser.pin || "").trim();
    const confirmPin = String(newUser.confirmPin || "").trim();
    const role = newUser.role === "admin" ? "admin" : "user";
    if (!username) {
      alert("Username is required.");
      return;
    }
    if (!pin) {
      alert("PIN is required.");
      return;
    }
    if (pin !== confirmPin) {
      alert("PIN fields do not match.");
      return;
    }
    setBusyId("__new__");
    try {
      const qRef = query(collection(db, "appUsers"), where("username", "==", username));
      const existing = await getDocs(qRef);
      if (!existing.empty) {
        alert("That username already exists.");
        return;
      }
      const payload = {
        username,
        pin,
        role,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: {
          userId: currentUser?.id || "",
          username: currentUser?.username || "",
          role: currentUser?.role || "",
        },
      };
      const ref = await addDoc(collection(db, "appUsers"), payload);
      setUsers((prev) => [{ id: ref.id, ...payload }, ...(prev || [])]);
      setNewUser({ username: "", pin: "", confirmPin: "", role: "user" });
    } catch (e2) {
      console.error("Error creating user:", e2);
      alert("Failed to create user. See console for details.");
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (u) => {
    if (!u?.id) return;
    const ok = window.confirm("Delete this user? This cannot be undone.");
    if (!ok) return;
    setBusyId(u.id);
    try {
      await deleteDoc(doc(db, "appUsers", u.id));
      setUsers((prev) => (prev || []).filter((row) => row.id !== u.id));
      if (editingId === u.id) cancelEdit();
    } catch (e) {
      console.error("Error deleting user:", e);
      alert("Failed to delete user. See console for details.");
    } finally {
      setBusyId(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="users-page">
        <h2>Users</h2>
        <p>You don’t have access to this tab.</p>
      </div>
    );
  }

  return (
    <div className="users-page">
      <h2>Users</h2>

      <div className="users-card">
        <div className="users-card-header">
          <div className="users-card-title">Create User</div>
          <label className="users-toggle">
            <input
              type="checkbox"
              checked={showPins}
              onChange={(e) => setShowPins(e.target.checked)}
            />
            Show PIN
          </label>
        </div>

        <form onSubmit={addUser} className="users-form">
          <div className="users-grid">
            <div className="users-field">
              <label>Username</label>
              <input
                value={newUser.username}
                onChange={(e) =>
                  setNewUser((p) => ({ ...p, username: e.target.value }))
                }
                placeholder="e.g. john"
              />
            </div>
            <div className="users-field">
              <label>PIN / Password</label>
              <input
                value={newUser.pin}
                onChange={(e) =>
                  setNewUser((p) => ({ ...p, pin: e.target.value }))
                }
                placeholder="Enter PIN"
                type={showPins ? "text" : "password"}
              />
            </div>
            <div className="users-field">
              <label>Confirm PIN</label>
              <input
                value={newUser.confirmPin}
                onChange={(e) =>
                  setNewUser((p) => ({ ...p, confirmPin: e.target.value }))
                }
                placeholder="Re-enter PIN"
                type={showPins ? "text" : "password"}
              />
            </div>
            <div className="users-field">
              <label>Role</label>
              <select
                value={newUser.role}
                onChange={(e) =>
                  setNewUser((p) => ({ ...p, role: e.target.value }))
                }
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>

          <div className="users-actions">
            <button className="btn-primary" type="submit" disabled={busyId === "__new__"}>
              {busyId === "__new__" ? "Creating..." : "Create User"}
            </button>
            <button className="btn-ghost" type="button" onClick={fetchUsers} disabled={loading}>
              Refresh
            </button>
          </div>
        </form>
      </div>

      {error && <div className="users-error">{error}</div>}
      {loading && <div className="users-muted">Loading…</div>}

      <div className="users-list">
        {sorted.map((u) => {
          const isEditing = editingId === u.id;
          return (
            <div key={u.id} className="users-row">
              <div className="users-row-top">
                <div className="users-row-title">
                  <strong>{u.username || "—"}</strong>{" "}
                  <span className="users-sub">({u.role || "user"})</span>{" "}
                  {u.active === false && <span className="users-inactive">(inactive)</span>}
                </div>
                <div className="users-row-actions">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={busyId === u.id}
                        onClick={() => saveEdit(u)}
                      >
                        {busyId === u.id ? "Saving..." : "Save"}
                      </button>
                      <button type="button" className="btn-ghost" onClick={cancelEdit}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="btn-ghost" onClick={() => startEdit(u)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-danger"
                        disabled={busyId === u.id}
                        onClick={() => deleteUser(u)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="users-edit">
                  <div className="users-grid">
                    <div className="users-field">
                      <label>Username</label>
                      <input
                        value={draft.username}
                        onChange={(e) =>
                          setDraft((p) => ({ ...p, username: e.target.value }))
                        }
                      />
                    </div>
                    <div className="users-field">
                      <label>PIN / Password</label>
                      <input
                        type={showPins ? "text" : "password"}
                        value={draft.pin}
                        onChange={(e) =>
                          setDraft((p) => ({ ...p, pin: e.target.value }))
                        }
                      />
                    </div>
                    <div className="users-field">
                      <label>Confirm PIN</label>
                      <input
                        type={showPins ? "text" : "password"}
                        value={draft.confirmPin}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            confirmPin: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="users-field">
                      <label>Role</label>
                      <select
                        value={draft.role}
                        onChange={(e) =>
                          setDraft((p) => ({ ...p, role: e.target.value }))
                        }
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                      <label className="users-toggle" style={{ marginTop: 10 }}>
                        <input
                          type="checkbox"
                          checked={draft.active !== false}
                          onChange={(e) =>
                            setDraft((p) => ({ ...p, active: e.target.checked }))
                          }
                        />
                        Active
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
